// Chat.tsx - Hero chat panel with modal popup integration
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import HeroChatPanel, { DEFAULT_PHRASES, PendingImageData } from './HeroChatPanel';
import Chatbox from '../ChatBox/ChatBox';
import './Chat.css';

// =============================================================================
// Page-specific phrase sets - import the one you need for each page
// =============================================================================
export const GENERAL_PHRASES = [
  "Verify my insurance coverage…",
  "What are the costs & benefits?",
  "Do you accept Blue Cross Blue Shield?",
  "Book an appointment for a cleaning…",
];

export const INSURANCE_PHRASES = [
  "Do you accept my insurance?",
  "What insurances do you take?",
  "Check my insurance benefits…",
  "Is Delta Dental accepted?",
];

export const APPOINTMENT_PHRASES = [
  "Book an appointment for a cleaning…",
  "What times are available this week?",
  "Schedule a root canal consultation…",
  "I need an emergency appointment…",
];

export const EMERGENCY_PHRASES = [
  "I have a dental emergency…",
  "My tooth is in severe pain…",
  "I chipped my tooth, what do I do?",
  "Emergency appointment availability?",
];

export const COSMETIC_PHRASES = [
  "How much does teeth whitening cost?",
  "Tell me about veneers…",
  "I want a brighter smile…",
  "What cosmetic options do you offer?",
];

export const PAYMENT_PHRASES = [
  "What payment options do you offer?",
  "Do you have financing available?",
  "Can I pay in installments?",
  "Tell me about CareCredit…",
];

export const PEDIATRIC_PHRASES = [
  "Do you see children?",
  "My child needs a dental checkup…",
  "Pediatric dental services?",
  "First dental visit for my toddler…",
];

// Re-export default phrases
export { DEFAULT_PHRASES };

// =============================================================================
// Chat Component Props
// =============================================================================
interface ChatProps {
  phrases?: string[];
  clinicName?: string;
}

// =============================================================================
// Main Chat Component
// =============================================================================
export default function Chat({ phrases, clinicName }: ChatProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string>('');
  const [pendingImages, setPendingImages] = useState<PendingImageData[]>([]);
  const chatboxRef = useRef<{ sendMessage: (message: string, images?: PendingImageData[]) => void } | null>(null);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (!isModalOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isModalOpen]);

  // Handle when user submits from the hero panel
  const handlePanelSubmit = useCallback((message: string, images?: PendingImageData[]) => {
    setPendingMessage(message);
    if (images && images.length > 0) {
      setPendingImages(images);
    }
    setIsModalOpen(true);
  }, []);

  // Handle when message has been processed by Chatbox
  const handleMessageProcessed = useCallback(() => {
    setPendingMessage('');
    setPendingImages([]);
  }, []);

  // Close modal
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setPendingMessage('');
    setPendingImages([]);
  }, []);

  const modalPortal =
    isModalOpen && typeof document !== 'undefined'
      ? createPortal(
          <div className="hero-chat-portal" role="presentation">
            <div className="modal-backdrop show" onClick={handleCloseModal} />
            <div
              className="modal show"
              role="dialog"
              aria-modal="true"
              style={{ display: 'block' }}
              onClick={handleCloseModal}
            >
              <div
                className="modal-dialog modal-fullscreen"
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className="modal-content glass-bg d-flex flex-column h-100"
                  style={{
                    backdropFilter: 'blur(8px)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <div className="modal-header border-0 text-white bg-dark bg-opacity-75">
                    <h5 className="modal-title">Tooth Fairy Chat</h5>
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-auto"
                      onClick={handleCloseModal}
                      aria-label="Close"
                    />
                  </div>
                  <Chatbox
                    ref={chatboxRef}
                    pendingMessage={pendingMessage}
                    pendingImages={pendingImages}
                    onMessageProcessed={handleMessageProcessed}
                  />
                </div>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      {/* Hero Chat Panel */}
      <HeroChatPanel
        phrases={phrases}
        clinicName={clinicName}
        onSubmit={handlePanelSubmit}
      />

      {/* Render modal at document.body level to avoid stacking-context issues */}
      {modalPortal}
    </>
  );
}
